import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import electrificationWhitepaperFull from "@/assets/electrification-whitepaper-full.png.asset.json";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ensureHubSpotScript, createHubSpotForm, HUBSPOT_LIGHT_CSS } from "@/lib/hubspot";

const HubSpotWhitePaperForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      ensureHubSpotScript(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = `<div id="whitepaper-electrification-hs-form"></div>`;
        createHubSpotForm({
          region: "eu1",
          portalId: "25538277",
          formId: "133ed114-29e4-4a2b-8840-f0c3c48a0ee4",
          target: "#whitepaper-electrification-hs-form",
          css: HUBSPOT_LIGHT_CSS,
        });
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}>
      <div ref={containerRef} className="min-h-[200px] [&_.hs-form-frame]:w-full" />
    </motion.div>
  );
};

const ease = [0.16, 1, 0.3, 1] as const;

const WhitePaperElectrificationDownload = () => {
  const { i18n } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>Download White Paper – Electrification | Cetasol</title>
        <meta name="description" content="Download our white paper: Is your fleet ready for electrification? The future of maritime operations." />
        <link rel="canonical" href="https://cetasol.com/whitepaper/electrification-download" />
        <meta property="og:title" content="Download White Paper – Electrification | Cetasol" />
        <meta property="og:description" content="Download our white paper: Is your fleet ready for electrification? The future of maritime operations." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://cetasol.com/whitepaper/electrification-download" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download White Paper – Electrification | Cetasol" />
        <meta name="twitter:description" content="Download our white paper: Is your fleet ready for electrification? The future of maritime operations." />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-0">
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <Link to="/whitepapers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ArrowLeft size={16} /> Back to all white papers
            </Link>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-7xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-[1.1]">
                  Download White Paper
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Fill out the form to download our white paper: <strong>Is your fleet ready for electrification? The future of maritime operations.</strong>
                </p>
                <img src={electrificationWhitepaperFull.url} alt="Electrification White Paper preview" className="mt-8 rounded-lg shadow-lg w-full max-w-[530px]" style={{ imageRendering: 'auto' }} />
              </motion.div>
              <HubSpotWhitePaperForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhitePaperElectrificationDownload;
